import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";

interface WatermarkModalProps {
  show: boolean;
  onHide: () => void;
  /** Current watermark text ("" / null = none). */
  value: string | null | undefined;
  /** Apply a watermark ("" clears it). */
  onApply: (text: string) => void;
}

const PRESETS = ["DRAFT", "CONFIDENTIAL", "PRIVILEGED", "ATTORNEY WORK PRODUCT"];

const WatermarkModal: React.FC<WatermarkModalProps> = ({
  show,
  onHide,
  value,
  onApply,
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState(value || "");

  useEffect(() => {
    if (show) setText(value || "");
  }, [show, value]);

  const apply = (v: string) => {
    onApply(v);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t("tiptapEditor.watermark.title", { defaultValue: "Watermark" })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "#6b7280",
              marginBottom: 6,
            }}
          >
            {t("tiptapEditor.watermark.presets", { defaultValue: "Presets" })}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {PRESETS.map(p => (
              <Button
                key={p}
                size="sm"
                variant={
                  (value || "").toUpperCase() === p
                    ? "success"
                    : "outline-secondary"
                }
                onClick={() => setText(p)}
              >
                {p}
              </Button>
            ))}
          </div>
        </div>

        <Form.Group>
          <Form.Label>
            {t("tiptapEditor.watermark.customText", {
              defaultValue: "Watermark text",
            })}
          </Form.Label>
          <Form.Control
            type="text"
            value={text}
            placeholder={t("tiptapEditor.watermark.placeholder", {
              defaultValue: "e.g. CONFIDENTIAL",
            })}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") apply(text.trim());
            }}
            autoFocus
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-danger"
          onClick={() => apply("")}
          disabled={!value}
        >
          {t("tiptapEditor.watermark.remove", { defaultValue: "Remove" })}
        </Button>
        <div style={{ flex: 1 }} />
        <Button variant="secondary" onClick={onHide}>
          {t("common.cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button variant="success" onClick={() => apply(text.trim())}>
          {t("tiptapEditor.watermark.apply", { defaultValue: "Apply" })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WatermarkModal;
